import React, { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { noop } from 'lodash/fp';
import { FormDataType } from 'contexts/FormProvider';
import { useContentContext } from '@qbe/dfc-content';
import { RELATIONSHIPS, QBEBUSINESSLIST } from '../enum';
import { DropdownController, FirstnameLastname, TextFieldController } from 'controllers';
import { Button, Dropdown, FormGroupLabel, TextField } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { FormSpacer, InformationPopUp } from 'components';
import { TrackEvent, formatNext } from 'hooks/useAnalytics';
import { useContentComplaintPartners } from 'utils/content';

interface Props {
  form: UseFormReturn<FormDataType>;
  isFieldRequired: (fieldName: keyof FormDataType) => boolean;
  showNextButton: boolean;
  isValid: boolean;
  onSubmit: () => void;
}

const PersonalDetails: FC<Props> = (props) => {
  const { form, isFieldRequired, showNextButton = true, isValid = false, onSubmit = noop } = props;

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const { control, getValues } = form;

  const handleClick = () => {
    TrackEvent(formatNext('nextContactDetails'), getValues());
    onSubmit();
  };

  return (
    <FormCard variant="sectionTitle" title={getTextContent('breach.personal.details.heading')}>
      <FormGroupLabel
        required={isFieldRequired('relationship')}
        title={getTextContent('breach.personal.details.relationship')}
        alignCenter
        legendElementOveride
        titleSuffix={
          <InformationPopUp
            message={getTextContent('fha.your.details.relationship.info')}
            data-track="infoIconRelationship"
          />
        }
      />
      <FormSpacer type="input"></FormSpacer>

      <DropdownController
        control={control}
        name="relationship"
        label="Relationship"
        capture
        optionList={RELATIONSHIPS.map((value) => ({
          label: value,
          value,
        }))}
      />

      <FormGroupLabel
        alignCenter
        legendElementOveride
        title={getTextContent('breach.personal.details.qbeBusiness')}
        required={isFieldRequired('qbeBusiness')}
      />
      <FormSpacer type="input" />
      <DropdownController
        control={control}
        name="qbeBusiness"
        label={getTextContent('breach.personal.details.qbeBusiness.label')}
        capture
        optionList={QBEBUSINESSLIST.map((value) => ({
          label: value,
          value,
        }))}
      />

      <FirstnameLastname
        form={form}
        maxLength={50}
        slotProps={{
          titleField: {
            title: getTextContent('consent.personalDetails.name'),
          },
        }}
      />

      <FormGroupLabel
        alignCenter
        legendElementOveride
        required={isFieldRequired('organisation')}
        title={getTextContent('consent.personalDetails.organisation')}
      />
      <FormSpacer type="input" />
      <TextFieldController
        control={control}
        maxLength={100}
        name="organisation"
        label={getTextContent('consent.personalDetails.organisation.label')}
        inputProps={{
          minLength: 1,
          maxLength: 100,
        }}
        capture
      />

      <FormSpacer type="formgroup" />

      {showNextButton && (
        <Button type="button" label={getTextContent('next.label')} disabled={!isValid} onClick={handleClick} />
      )}
    </FormCard>
  );
};

export default PersonalDetails;
